import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Persistent bottom tab bar for the Kids Zone — mounted once by KidsShell
// so it survives navigation between the hub, watch zone, and progress
// screens. Three real destinations, not four: the Games Hub already opens
// on a "Hi, {child}!" greeting with stats and a continue-learning shelf,
// so it doubles as Home rather than needing a separate landing page.
//
// RTL: the row itself needs no `rtl:` handling — `justify-content` and
// plain flex order already mirror automatically once <html dir="rtl"> is
// set (see App.js). Only the physical `inset-x` positioning below uses a
// symmetric value so it never needs a variant either.
const TABS = [
  { key: "home", path: "/kids/games", icon: HomeIcon },
  { key: "watch", path: "/kids/watch", icon: WatchIcon },
  { key: "progress", path: "/kids/achievements", icon: ProgressIcon },
];

export default function KidsBottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const childId = params.get("childId") || sessionStorage.getItem("childId") || "";
  const qs = childId ? `?childId=${childId}` : "";

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 px-3.5 pt-2 pointer-events-none lg:hidden"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 10px)" }}
      aria-label={t("kids.nav.home")}
    >
      <div
        className="mx-auto max-w-md flex items-center justify-around bg-ground rounded-[24px] py-2 px-3
          border border-panel-line shadow-sheet pointer-events-auto"
      >
        {TABS.map(({ key, path, icon: Icon }) => {
          const active = location.pathname === path || location.pathname.startsWith(path + "/");
          return (
            <button
              key={key}
              onClick={() => navigate(`${path}${qs}`)}
              className="flex flex-col items-center gap-0.5 min-w-[56px] py-0.5"
            >
              <span
                className={`w-[38px] h-[38px] rounded-[13px] flex items-center justify-center transition ${
                  active ? "bg-brand shadow-primary -translate-y-0.5" : ""
                }`}
              >
                <Icon active={active} />
              </span>
              <span className={`font-body text-[10px] font-extrabold ${active ? "text-ink" : "text-faint"}`}>
                {t(`kids.nav.${key}`)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

const stroke = { fill: "none", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" };

function HomeIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path d="M4 11.5 12 5l8 6.5M6.5 10.5V19h11v-8.5" stroke={active ? "#fff" : "#B0A290"} {...stroke} />
    </svg>
  );
}

function WatchIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9" stroke={active ? "#fff" : "#B0A290"} {...stroke} />
      <path d="M10 8.5v7l6-3.5-6-3.5Z" stroke={active ? "#fff" : "#B0A290"} {...stroke} />
    </svg>
  );
}

function ProgressIcon({ active }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path d="M5 19V11M12 19V5M19 19v-7" stroke={active ? "#fff" : "#B0A290"} {...stroke} />
    </svg>
  );
}
